
// @material-ui core components
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
// @mui material icon
import EditIcon from '@mui/icons-material/Edit';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Settings page components
import FormField from 'layouts/pages/account/components/FormField';

// Data
import { Button } from '@mui/material';
import { useToggle } from 'hooks/useToggle';
import { useEffect, useState } from 'react';
import { getUserById, patchUsuarioData, verifyCurrentPassword } from 'actions/MiCuenta/miCuenta';
import { useSelector } from 'react-redux';
import { RootStore } from 'store/Store';
import Swal from 'sweetalert2';

function BasicInfo(): JSX.Element {
	const { id } = useSelector((state: RootStore) => state.auth);
	const [value, toggleValue] = useToggle(true); //Recibe el valor inicial

    interface iMicuentaFormulario {
        miNombre: string;
        miEmail: string;
        currentPassword: string;
        newPassword: string;
        confirmPassword: string;
    }
    const miCuenta: iMicuentaFormulario = {
        miNombre: '',
        miEmail: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    };
	const [formValues, setFormValues] = useState(miCuenta);
    const { miNombre, miEmail, currentPassword, newPassword, confirmPassword } = formValues;

	const handleInputChange = (event: any) => {
		setFormValues({
			...formValues,
			[event.target.name]: event.target.value,
		});
	};

	useEffect(() => {
		const dataUsuario = getUserById(id);
		dataUsuario
			.then((respuesta) => {
            if (respuesta.status === 201) {
                setFormValues({
                    miNombre: respuesta.data.data.nombre,
                    miEmail: respuesta.data.data.email,
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                });
            }
			})
			.catch((error) => {
				if (error.response.status === 500) {
					console.log('error500');
					console.log(error.response.data.message);
				} else if (error.response.status === 400) {
					console.log('error400');
					console.log(error.response.data.message);
				} else if (error.response.status === 403) {
					console.log('error403');
					console.log(error.response.data.message);
				} else if (error.response.status === 404) {
					console.log('error404');
					console.log(error.response.data.message);
				} else {
					console.log(error.response.data.message);
				}
			});
	}, [id]);

    const handleSubmit = async (e: any) => {
        e.preventDefault();

        // Armar payload básico
        const payload: any = { nombre: miNombre, email: miEmail };

        // Si se pretenden cambiar contraseñas, validar primero
        const quiereCambiarPassword =
            currentPassword.trim() !== '' || newPassword.trim() !== '' || confirmPassword.trim() !== '';

        if (quiereCambiarPassword) {
            // Validaciones de front
            if (!currentPassword || !newPassword || !confirmPassword) {
                Swal.fire({ icon: 'error', title: 'Completa todos los campos de contraseña' });
                return;
            }
            if (newPassword.length < 6) {
                Swal.fire({ icon: 'error', title: 'La nueva contraseña debe tener mínimo 6 caracteres' });
                return;
            }
            if (newPassword !== confirmPassword) {
                Swal.fire({ icon: 'error', title: 'La confirmación no coincide con la nueva contraseña' });
                return;
            }
            try {
                // Verificar contraseña actual reautenticando contra /auth
                await verifyCurrentPassword(miEmail, currentPassword);
                // Si pasó la verificación, incluir nueva contraseña en el patch
                payload.password = newPassword;
            } catch (err: any) {
                const msg = err?.response?.data || 'Contraseña actual incorrecta';
                Swal.fire({ icon: 'error', title: msg });
                return;
            }
        }

        patchUsuarioData(payload, id)
            .then((respuesta) => {
                if (respuesta.status === 201) {
                    const updated = respuesta.data?.data || respuesta.data;
                    setFormValues({
                        miNombre: updated.nombre,
                        miEmail: updated.email,
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: '',
                    });
                    toggleValue();
                    Swal.fire({
                        position: 'top-end',
                        icon: 'success',
                        title: `¡Actualización exitosa!`,
                        showConfirmButton: false,
                        timer: 2000,
                    });
                }
            })
            .catch((error) => {
                if (error.response?.status === 500) {
                    console.log('error500');
                    console.log(error.response.data.message);
                } else if (error.response?.status === 400) {
                    console.log('error400');
                    console.log(error.response.data.message);
                } else if (error.response?.status === 403) {
                    console.log('error403');
                    console.log(error.response.data.message);
                } else if (error.response?.status === 404) {
                    console.log('error404');
                    console.log(error.response);
                    toggleValue();
                    Swal.fire({
                        position: 'top-end',
                        icon: 'error',
                        title: `${error.response.data.msg}`,
                        showConfirmButton: false,
                        timer: 3500,
                    });
                } else {
                    console.log(error);
                }
            });
    };
	const cancelarEdicion = () => {
		toggleValue();
	};

	return (
		<Card id='basic-info' sx={{ overflow: 'visible', marginBottom: '3rem' }}>
			<MDBox p={3}>
				<MDTypography variant='h5'>
					Mi Cuenta
					<Button
						color='secondary'
						variant='text'
						size='large'
						startIcon={<EditIcon />}
						onClick={toggleValue}
					>
						Editar
					</Button>
				</MDTypography>
				<MDTypography variant='caption'>
					Todos los campos con * son obligatorios
				</MDTypography>
			</MDBox>
        <MDBox component='form' pb={3} px={3} onSubmit={handleSubmit}>
            <Grid container spacing={2}>
					<Grid item xs={12} sm={3}>
						<FormField
							label='Nombre de usuario'
							placeholder='Nombre de usuario'
							name='miNombre'
							value={miNombre}
							onChange={handleInputChange}
							disabled={value}
							required
						/>
					</Grid>
					<Grid item xs={12} sm={3}>
						<FormField
							type='text'
							label='Correo electrónico*'
							placeholder='Correo electrónico*'
							name='miEmail'
							value={miEmail}
							onChange={handleInputChange}
							disabled={value}
							required
						/>
                </Grid>
                {!value && (
                    <>
                        <Grid item xs={12} sm={3}>
                            <FormField
                                type='password'
                                label='Contraseña actual'
                                placeholder='Contraseña actual'
                                name='currentPassword'
                                value={currentPassword}
                                onChange={handleInputChange}
                                disabled={value}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormField
                                type='password'
                                label='Nueva contraseña'
                                placeholder='Nueva contraseña'
                                name='newPassword'
                                value={newPassword}
                                onChange={handleInputChange}
                                disabled={value}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormField
                                type='password'
                                label='Confirmar nueva contraseña'
                                placeholder='Confirmar nueva contraseña'
                                name='confirmPassword'
                                value={confirmPassword}
                                onChange={handleInputChange}
                                disabled={value}
                            />
                        </Grid>
                    </>
                )}
                {!value && (
                    <Grid item xs={12} sm={3}>
                        <Button
								color='secondary'
								variant='outlined'
								size='small'
								style={{ color: '#bdbdbd', margin: '5px' }}
								onClick={cancelarEdicion}
							>
								Cancelar
							</Button>
							<Button
								type='submit'
								color='primary'
								variant='contained'
								size='small'
								style={{ color: 'white', margin: '5px' }}
							>
								Editar
							</Button>
						</Grid>
					)}
				</Grid>
			</MDBox>
		</Card>
	);
}

export default BasicInfo;
